// Pure JavaScript animation controller - no React dependencies
// Timeline and PreviewFrame communicate through this controller

export interface AnimationDisplayState {
  currentTimeMs: number;
  durationMs: number;
  isPlaying: boolean;
  isLooping: boolean;
  isMuted: boolean;
  volume: number;
  progress: number; // 0-1, computed from currentTimeMs/durationMs
}

type DisplayListener = (state: AnimationDisplayState) => void;

export class AnimationController {
  // Internal state
  private _isPlaying = false;
  private _isLooping = true;
  private _isMuted = false;
  private _volume = 0.7;
  private _durationMs = 3000;
  private _currentTimeMs = 0;

  // Callbacks
  onPlaybackComplete: (() => void) | null = null;
  onPause: (() => void) | null = null;

  // RAF state
  private lastFrameTime: number | null = null;
  private rafId: number | null = null;

  // Rendering target
  private iframe: HTMLIFrameElement | null = null;

  // Display listeners (for Timeline DOM updates)
  private displayListeners: Set<DisplayListener> = new Set();
  private lastEmitTime = 0;
  private readonly EMIT_INTERVAL = 16; // ~60fps for smooth timeline

  // Cached state for useSyncExternalStore (must return same reference if unchanged)
  private cachedState: AnimationDisplayState | null = null;

  // Async operation tracking
  private pendingSeek: { resolve: () => void; reject: (e: Error) => void } | null = null;
  private pendingInit: { resolve: () => void; reject: (e: Error) => void } | null = null;
  private messageHandler: ((event: MessageEvent) => void) | null = null;

  // Getters
  get isPlaying() {
    return this._isPlaying;
  }
  get isLooping() {
    return this._isLooping;
  }
  get isMuted() {
    return this._isMuted;
  }
  get volume() {
    return this._volume;
  }
  get durationMs() {
    return this._durationMs;
  }
  get currentTimeMs() {
    return this._currentTimeMs;
  }
  get progress() {
    return this._durationMs > 0 ? this._currentTimeMs / this._durationMs : 0;
  }

  // Arrow function for useSyncExternalStore compatibility
  // Returns cached state to avoid infinite loops
  getState = (): AnimationDisplayState => {
    if (!this.cachedState) {
      this.cachedState = this.buildState();
    }
    return this.cachedState;
  };

  private buildState(): AnimationDisplayState {
    return {
      currentTimeMs: this._currentTimeMs,
      durationMs: this._durationMs,
      isPlaying: this._isPlaying,
      isLooping: this._isLooping,
      isMuted: this._isMuted,
      volume: this._volume,
      progress: this.progress,
    };
  }

  // Arrow function for useSyncExternalStore compatibility
  subscribe = (listener: DisplayListener): (() => void) => {
    this.displayListeners.add(listener);
    return () => this.displayListeners.delete(listener);
  };

  private emit(force = false) {
    const now = performance.now();
    if (!force && now - this.lastEmitTime < this.EMIT_INTERVAL) return;
    this.lastEmitTime = now;

    // Update cached state before notifying listeners
    this.cachedState = this.buildState();
    for (const listener of this.displayListeners) {
      listener(this.cachedState);
    }
  }

  setIframe(iframe: HTMLIFrameElement | null) {
    // Clean up old message handler
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }

    this.iframe = iframe;

    if (iframe) {
      // Set up message handler for async operation completions
      this.messageHandler = (event: MessageEvent) => {
        const { type } = event.data;
        if (type === "INIT_COMPLETE") {
          if (this.pendingInit) {
            this.pendingInit.resolve();
            this.pendingInit = null;
          }
          // Sync volume, media, render frame, and resume playback if was playing
          this.notifyVolume();
          this.notifySeek().then(() => {
            this.renderFrame();
            if (this._isPlaying) {
              this.notifyResume();
            }
          });
        } else if (type === "SEEK_COMPLETE") {
          if (this.pendingSeek) {
            this.pendingSeek.resolve();
            this.pendingSeek = null;
          }
        }
      };
      window.addEventListener("message", this.messageHandler);

      this.renderFrame();
    }
  }

  setDuration(durationSec: number) {
    this._durationMs = durationSec * 1000;
    if (this._currentTimeMs > this._durationMs) {
      this._currentTimeMs = this._durationMs;
    }
    this.emit(true);
  }

  setLooping(isLooping: boolean) {
    this._isLooping = isLooping;
    this.emit(true);
  }

  setMuted(isMuted: boolean) {
    this._isMuted = isMuted;
    if (!isMuted && this._volume === 0) {
      this._volume = 0.7; // Restore default volume when unmuting from 0
    }
    this.notifyVolume();
    this.emit(true);
  }

  setVolume(volume: number) {
    this._volume = Math.max(0, Math.min(1, volume));
    this._isMuted = this._volume === 0;
    this.notifyVolume();
    this.emit(true);
  }

  async seek(timeMs: number): Promise<void> {
    this._currentTimeMs = Math.max(0, Math.min(timeMs, this._durationMs));
    await this.notifySeek();
    this.renderFrame();
    this.emit(true);
  }

  async seekProgress(progress: number): Promise<void> {
    await this.seek(progress * this._durationMs);
  }

  async play(): Promise<void> {
    if (this._isPlaying) return;
    // If at end and not looping, restart
    if (this._currentTimeMs >= this._durationMs && !this._isLooping) {
      this._currentTimeMs = 0;
    }
    this._isPlaying = true;
    this.lastFrameTime = null;
    await this.notifySeek();
    this.emit(true);
    this.tick();
  }

  pause() {
    if (!this._isPlaying) return;
    this._isPlaying = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.onPause?.();
    this.notifyPause();
    this.emit(true);
  }

  async togglePlay(): Promise<void> {
    if (this._isPlaying) {
      this.pause();
    } else {
      await this.play();
    }
  }

  async reset(): Promise<void> {
    await this.seek(0);
  }

  dispose() {
    this.pause();
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }
    this.iframe = null;
    this.displayListeners.clear();
  }

  private tick = () => {
    if (!this._isPlaying) return;

    const now = performance.now();
    if (this.lastFrameTime !== null) {
      const deltaMs = now - this.lastFrameTime;
      this._currentTimeMs += deltaMs;

      if (this._currentTimeMs >= this._durationMs) {
        this.onPlaybackComplete?.();
        if (this._isLooping) {
          this._currentTimeMs = this._currentTimeMs % this._durationMs;
          this.notifySeek();
        } else {
          this._currentTimeMs = this._durationMs;
          this._isPlaying = false;
          this.renderFrame();
          this.emit(true);
          return;
        }
      }
    }
    this.lastFrameTime = now;

    this.renderFrame();
    this.emit();

    if (this._isPlaying) {
      this.rafId = requestAnimationFrame(this.tick);
    }
  };

  private async notifySeek(): Promise<void> {
    if (!this.iframe?.contentWindow) return;

    // Create promise for seek completion
    const seekPromise = new Promise<void>((resolve, reject) => {
      this.pendingSeek = { resolve, reject };
      // Timeout after 5 seconds
      setTimeout(() => {
        if (this.pendingSeek) {
          this.pendingSeek = null;
          resolve(); // Resolve anyway to not block forever
        }
      }, 5000);
    });

    this.iframe.contentWindow.postMessage(
      { type: "SEEK", data: { time: this._currentTimeMs, isPlaying: this._isPlaying } },
      "*",
    );

    await seekPromise;
  }

  private notifyPause() {
    if (!this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage({ type: "PAUSE" }, "*");
  }

  private notifyResume() {
    if (!this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage({ type: "RESUME" }, "*");
  }

  private notifyVolume() {
    if (!this.iframe?.contentWindow) return;
    const effectiveVolume = this._isMuted ? 0 : this._volume;
    this.iframe.contentWindow.postMessage(
      { type: "SET_VOLUME", data: { volume: effectiveVolume } },
      "*",
    );
  }

  private renderFrame() {
    if (!this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage(
      { type: "RENDER", data: { time: this._currentTimeMs, isPlaying: this._isPlaying } },
      "*",
    );
  }
}
